import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { startRepaint, getCreation, errorText, REPAINT_STALE_MS } from "../lib/api";
import { playQuack } from "../lib/quack";

const BASE_MS = 2500;
const MAX_MS = 6000;

// Drives the "Repainting…" overlay from the server-persisted `repaint_status`. Seeds itself from the creation it is
// handed, so a page reload mid-repaint immediately resumes the overlay and polling (status lives on the duck, not here).
export const useRepaint = (creation, { onUpdate } = {}) => {
  const id = creation?.id;
  const [running, setRunning] = useState(creation?.repaint_status === "running");
  const [error, setError] = useState(
    creation?.repaint_status === "failed" ? creation?.repaint_error?.reason || "Repaint failed — tap to retry." : null,
  );
  const timer = useRef(null);
  const alive = useRef(true);
  const startedAt = useRef(creation?.repaint_started_at || null);

  const stop = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const poll = useCallback(
    (delay = BASE_MS) => {
      stop();
      timer.current = setTimeout(async () => {
        if (!alive.current) return;
        try {
          const doc = await getCreation(id);
          if (!alive.current) return;
          if (doc.repaint_status === "running") {
            startedAt.current = doc.repaint_started_at || startedAt.current;
            // client guard so the overlay is never stuck past the server's ~12-min stale sweep
            if (startedAt.current && Date.now() - new Date(startedAt.current).getTime() > REPAINT_STALE_MS) {
              setRunning(false);
              setError("Repaint timed out — tap to retry.");
              return;
            }
            poll(Math.min(delay + 500, MAX_MS));
          } else if (doc.repaint_status === "failed") {
            setRunning(false);
            setError(doc.repaint_error?.reason || "Repaint failed — tap to retry.");
          } else {
            setRunning(false);
            setError(null);
            // Step 03 — a repaint finished (no sound on failure). Keyed by the completion time so each repaint quacks
            // once, but a later repaint of the same duck can quack again.
            playQuack("repaint", { once: `${id}:repaint:${doc.repainted_at || startedAt.current || ""}` });
            onUpdate?.(doc);
          }
        } catch {
          if (alive.current) poll(Math.min(delay + 1000, MAX_MS)); // transient network hiccup: back off and retry
        }
      }, delay);
    },
    [id, onUpdate, stop],
  );

  useEffect(() => {
    alive.current = true;
    if (creation?.repaint_status === "running") {
      setRunning(true);
      poll(BASE_MS);
    }
    return () => {
      alive.current = false;
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const run = useCallback(
    async (opts = {}) => {
      setError(null);
      setRunning(true);
      try {
        const doc = await startRepaint(id, opts);
        startedAt.current = doc?.repaint_started_at || new Date().toISOString();
        poll(BASE_MS);
        return true;
      } catch (e) {
        if (e?.response?.status === 409) {
          // already running (e.g. a second tab): just follow it to completion
          startedAt.current = startedAt.current || new Date().toISOString();
          poll(BASE_MS);
          return true;
        }
        setRunning(false);
        const msg = errorText(e, "Could not start the repaint — please try again.");
        setError(msg);
        toast.error(msg);
        return false;
      }
    },
    [id, poll],
  );

  return { running, error, run, retry: run };
};
